import { memo, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Box,
  Typography,
} from "@mui/material";

const DATA_CLASSIFICATION_OPTIONS = ["public", "internal", "confidential", "highly-restricted"];
const DEPLOYED_BY_OPTIONS = ["terraform", "cloudformation", "manual"];

const normalizeTag = (tag) => {
  return tag
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 40);
};

const ProjectDialog = memo(({ open, onClose, onSave, projects, isAdmin, mode, initialRecord }) => {
  const getInitialRecord = () =>
    mode === "create" ? { Active: true, Reports: false } : (initialRecord || {});

  const [recordToEdit, setRecordToEdit] = useState(getInitialRecord);
  const [projectFormErrors, setProjectFormErrors] = useState({
    projectError: "",
    speedtypeError: "",
    supportContactError: "",
    ownerError: "",
    dataClassificationError: "",
    deployedByError: "",
    terraformStackError: "",
  });

  useEffect(() => {
    if (open) {
      setRecordToEdit(getInitialRecord());
      setProjectFormErrors({
        projectError: "",
        speedtypeError: "",
        supportContactError: "",
        ownerError: "",
        dataClassificationError: "",
        deployedByError: "",
        terraformStackError: "",
      });
    }
  }, [open, mode, initialRecord]);

  function validateProject(record, isEdit) {
    const errors = {};

    record.Project = record.Project.replace(/^-+|-+$/g, "");

    if (!record.Project) {
      errors.projectError = "You must enter a project name.";
    } else if (
      projects.some(
        (p) => normalizeTag(p.Project) === normalizeTag(record.Project)
      ) && !isEdit
    ) {
      errors.projectError = "This project name is already in use.";
    }

    if (!record.SpeedType) {
      errors.speedtypeError = "You must enter a speedtype.";
    }

    const emailInput = record.SupportContact
      ? record.SupportContact.trim()
      : "";
    if (!emailInput) {
      errors.supportContactError = "You must enter a support contact.";
    } else {
      const domainRegex = /^[^\s@]+@(umassmed\.edu|uhealthsolutions\.org)$/i;
      if (!domainRegex.test(emailInput)) {
        errors.supportContactError = "Must be a valid @umassmed.edu or @uhealthsolutions.org email.";
      }
    }

    if (!record.ExcludeSharedAdmin) {
      record.ExcludeSharedAdmin = false;
    }

    if (!record.Owner || !record.Owner.trim()) {
      errors.ownerError = "You must enter an owner email.";
    } else {
      const ownerEmail = record.Owner.trim().toLowerCase();
      record.Owner = ownerEmail;
      const ownerDomainRegex = /^[^\s@]+@(umassmed\.edu|uhealthsolutions\.org)$/i;
      if (!ownerDomainRegex.test(ownerEmail)) {
        errors.ownerError = "Owner must be a valid @umassmed.edu or @uhealthsolutions.org email.";
      }
    }

    if (!record.DataClassification) {
      errors.dataClassificationError = "You must select a data classification.";
    }

    if (!record.DeployedBy) {
      errors.deployedByError = "You must select a deployment method.";
    }

    if (record.DeployedBy === "terraform") {
      if (!record.TerraformStack || !record.TerraformStack.trim()) {
        errors.terraformStackError = "TerraformStack is required when DeployedBy is terraform.";
      } else if (!/^[^/]+\/[^/]+\/[^/]+$/.test(record.TerraformStack.trim())) {
        errors.terraformStackError = "TerraformStack must follow the format: org/project/repo";
      }
    }

    if (record.DeployedBy !== "terraform") {
      record.TerraformStack = "";
    }

    return { record, errors };
  }

  const enforceNamingRule = (event) => {
    const normalized = normalizeTag(event.target.value);
    setRecordToEdit((prevState) => ({
      ...prevState,
      [event.target.id]: normalized,
    }));
  };

  const handleFieldUpdate = (event) => {
    setRecordToEdit((prevState) => ({
      ...prevState,
      [event.target.id]: event.target.value,
    }));
  };

  const handleEmailChange = (event) => {
    setRecordToEdit((prevState) => ({
      ...prevState,
      [event.target.id]: event.target.value.toLowerCase(),
    }));
  };

  const handleToggleUpdate = (event) => {
    setRecordToEdit((prevState) => ({
      ...prevState,
      [event.target.id]: !prevState[event.target.id],
    }));
  };

  const handleSelectUpdate = (event) => {
    const { name, value } = event.target;
    setRecordToEdit((prevState) => ({
      ...prevState,
      [name]: value,
      ...(name === "DeployedBy" && value !== "terraform" ? { TerraformStack: "" } : {}),
    }));
  };

  const handleSave = () => {
    const { record, errors } = validateProject({ ...recordToEdit }, mode === "edit");

    setProjectFormErrors((prev) => ({ ...prev, ...errors }));

    if (Object.keys(errors).length === 0) {
      onSave(record);
    }
  };

  const handleCancel = () => {
    onClose();
    if (mode === "create") {
      setRecordToEdit({ Active: true, Reports: false });
    }
    setProjectFormErrors({
      projectError: "",
      speedtypeError: "",
      supportContactError: "",
      ownerError: "",
      dataClassificationError: "",
      deployedByError: "",
      terraformStackError: "",
    });
  };

  const isCreate = mode === "create";

  return (
    <Dialog
      {...(mode === "edit" ? { className: "dialog" } : {})}
      open={open}
      onClose={handleCancel}
    >
      <DialogTitle>
        {isCreate
          ? `New Project${recordToEdit.Project ? ": " + recordToEdit.Project : ""}`
          : `Edit Project: ${recordToEdit.Project}`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText></DialogContentText>

        {isCreate ? (
          <TextField
            onBlur={handleFieldUpdate}
            onChange={enforceNamingRule}
            autoFocus
            margin="dense"
            id="Project"
            label="Project"
            type="text"
            fullWidth
            variant="standard"
            value={recordToEdit.Project || ""}
            required
            error={projectFormErrors.projectError.length > 0}
            helperText={
              projectFormErrors.projectError || "Lowercase, hyphen-separated, no spaces. Should match code repository name."
            }
          />
        ) : (
          <TextField
            margin="dense"
            id="Project"
            label="Project"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={recordToEdit.Project}
            disabled={true}
            required
            error={projectFormErrors.projectError.length > 0}
            helperText={projectFormErrors.projectError}
          />
        )}

        <TextField
          onBlur={handleFieldUpdate}
          {...(isCreate ? { autoFocus: true } : {})}
          margin="dense"
          id="SpeedType"
          label="SpeedType"
          type="text"
          fullWidth
          variant="standard"
          defaultValue={recordToEdit.SpeedType}
          required
          error={projectFormErrors.speedtypeError.length > 0}
          helperText={
            projectFormErrors.speedtypeError || "6-digit UMass funding source code."
          }
        />
        <TextField
          onChange={handleEmailChange}
          {...(isCreate ? { autoFocus: true } : {})}
          margin="dense"
          id="SupportContact"
          label="SupportContact"
          type="text"
          fullWidth
          variant="standard"
          value={recordToEdit.SupportContact || ""}
          required
          error={projectFormErrors.supportContactError.length > 0}
          helperText={
            projectFormErrors.supportContactError || "Lowercase email (e.g., user@umassmed.edu)"
          }
        />

        <FormControl fullWidth variant="standard" margin="dense" required error={projectFormErrors.dataClassificationError?.length > 0}>
          <InputLabel id={`${isCreate ? "new-" : ""}DataClassification-label`}>DataClassification</InputLabel>
          <Select
            labelId={`${isCreate ? "new-" : ""}DataClassification-label`}
            name="DataClassification"
            value={recordToEdit.DataClassification || ""}
            onChange={handleSelectUpdate}
            label="DataClassification"
          >
            {DATA_CLASSIFICATION_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
          <FormHelperText>{projectFormErrors.dataClassificationError || "Sensitivity level of data associated with this project."}</FormHelperText>
        </FormControl>

        <TextField
          onChange={handleEmailChange}
          margin="dense"
          id="Owner"
          label="Owner"
          type="email"
          fullWidth
          variant="standard"
          value={recordToEdit.Owner || ""}
          required
          error={projectFormErrors.ownerError?.length > 0}
          helperText={projectFormErrors.ownerError || "Business owner. Lowercase email (e.g., owner@umassmed.edu)."}
        />

        <FormControl fullWidth variant="standard" margin="dense" required error={projectFormErrors.deployedByError?.length > 0}>
          <InputLabel id={`${isCreate ? "new-" : ""}DeployedBy-label`}>DeployedBy</InputLabel>
          <Select
            labelId={`${isCreate ? "new-" : ""}DeployedBy-label`}
            name="DeployedBy"
            value={recordToEdit.DeployedBy || ""}
            onChange={handleSelectUpdate}
            label="DeployedBy"
          >
            {DEPLOYED_BY_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
          <FormHelperText>{projectFormErrors.deployedByError || "Deployment mechanism used for this project's resources."}</FormHelperText>
        </FormControl>

        {recordToEdit.DeployedBy === "terraform" && (
          <TextField
            onBlur={handleFieldUpdate}
            margin="dense"
            id="TerraformStack"
            label="TerraformStack"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={recordToEdit.TerraformStack}
            error={projectFormErrors.terraformStackError?.length > 0}
            helperText={projectFormErrors.terraformStackError || "Format: azure-devops-org/ado-project-name/ado-repository-name"}
          />
        )}

        <Box sx={{ display: "block", mt: 1 }}>
          <FormControlLabel
            label="Active"
            control={
              <Switch
                id="Active"
                onChange={handleToggleUpdate}
                checked={recordToEdit.Active || false}
              />
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
            Whether this project is currently active. Inactive projects disable Reports and Exclude toggles.
          </Typography>
        </Box>

        <Box sx={{ display: "block", mt: 1 }}>
          <FormControlLabel
            label="Reports"
            control={
              <Switch
                id="Reports"
                onChange={handleToggleUpdate}
                checked={recordToEdit.Reports || false}
                disabled={!isCreate && !recordToEdit.Active}
              />
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
            Send monthly billing CSV reports for this project.
          </Typography>
        </Box>

        {isAdmin && (
          <Box sx={{ display: "block", mt: 1 }}>
            <FormControlLabel
              label="ExcludeSharedAdmin"
              control={
                <Switch
                  id="ExcludeSharedAdmin"
                  onChange={handleToggleUpdate}
                  checked={recordToEdit.ExcludeSharedAdmin || false}
                  disabled={!isCreate && !recordToEdit.Active}
                />
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
              Exclude this project from shared cost allocations.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={
            !recordToEdit.Project ||
            !recordToEdit.SpeedType ||
            !recordToEdit.SupportContact ||
            !recordToEdit.DataClassification ||
            !recordToEdit.Owner ||
            !recordToEdit.DeployedBy
          }
          onClick={handleSave}
        >
          Save
        </Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
});

export default ProjectDialog;
